import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogService, RentalAgreement, RentalAssetUnit } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-rental-agreement-detail',
  imports: [
    DecimalPipe, DatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule,
    ButtonModule, InputTextModule, SelectModule, TextareaModule, InputNumberModule
  ],
  template: `
    <ain-page
      [title]="isCreateMode() ? t('catalog.rental.agreements.createTitle') : t('catalog.rental.agreements.detailTitle')"
      [description]="t('catalog.rental.agreements.detailDescription')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!isCreateMode() && !agreement()) {
        <ain-state type="error" [title]="t('catalog.unavailable')" [message]="error() || t('catalog.rental.notFound')" />
      } @else {
        <section class="header-card">
          <div>
            <h3>{{ isCreateMode() ? t('catalog.rental.agreements.newRecord') : agreement()!.agreementNumber }}</h3>
            <p>{{ isCreateMode() ? t('catalog.rental.agreements.createHint') : agreement()!.status }}</p>
          </div>
          <div class="actions">
            <a [routerLink]="['/rental/agreements']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('catalog.action.save')" [disabled]="saving()" (click)="save()"></button>
            }
          </div>
        </section>

        @if (isCreateMode()) {
          <section class="form-grid">
            <label><span>{{ t('catalog.rental.agreementNumber') }}</span><input pInputText [(ngModel)]="form.agreementNumber" /></label>
            <label><span>{{ t('catalog.service.customerReference') }}</span><input pInputText [(ngModel)]="form.customerReference" /></label>
            <label><span>{{ t('catalog.rental.unit') }}</span>
              <p-select [options]="unitOptions()" [(ngModel)]="form.rentalAssetUnitId" optionLabel="label" optionValue="value" [filter]="true" filterBy="label" [showClear]="true" appendTo="body" />
            </label>
            <label><span>{{ t('catalog.rental.depositAmount') }}</span><p-inputNumber [(ngModel)]="form.depositAmount" [min]="0" /></label>
            <label><span>{{ t('catalog.rental.rateAmount') }}</span><p-inputNumber [(ngModel)]="form.rentalRateAmount" [min]="0" /></label>
          </section>
        } @else {
          <section class="summary-grid">
            <article><strong>{{ t('catalog.rental.depositAmount') }}</strong><span>{{ agreement()!.depositAmount | number:'1.2-2' }}</span></article>
            <article><strong>{{ t('catalog.rental.rateAmount') }}</strong><span>{{ agreement()!.rentalRateAmount | number:'1.2-2' }}</span></article>
            <article><strong>{{ t('catalog.rental.start') }}</strong><span>{{ agreement()!.startUtc | date:'short' }}</span></article>
            <article><strong>{{ t('catalog.rental.end') }}</strong><span>{{ agreement()!.endUtc | date:'short' }}</span></article>
          </section>

          @if (agreement()!.handover) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.handover') }}</h4>
              <p>{{ t('catalog.rental.lifecycle.handoverAt') }}: {{ agreement()!.handover!.handoverAtUtc | date:'short' }}</p>
              <p>{{ t('catalog.rental.lifecycle.signature') }}: {{ agreement()!.handover!.signatureReference }}</p>
            </section>
          }

          @if (agreement()!.returnChecklist) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.return') }}</h4>
              <p>{{ t('catalog.rental.lifecycle.returnAt') }}: {{ agreement()!.returnChecklist!.returnAtUtc | date:'short' }}</p>
              <p>{{ t('catalog.rental.lifecycle.signature') }}: {{ agreement()!.returnChecklist!.signatureReference }}</p>
            </section>
          }

          @if (agreement()!.damageAssessment) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.damage') }}</h4>
              <p>{{ t('catalog.rental.lifecycle.damageAmount') }}: {{ agreement()!.damageAssessment!.damageAmount | number:'1.2-2' }}</p>
              <p>{{ t('catalog.rental.lifecycle.damageStatus') }}: {{ agreement()!.damageAssessment!.status }}</p>
              <p>{{ agreement()!.damageAssessment!.description }}</p>
              @if (canApproveDamage()) {
                <button pButton type="button" [label]="t('catalog.rental.action.approveDamage')" [disabled]="acting()" (click)="approveDamage()"></button>
              }
            </section>
          }

          @if (agreement()!.settlement) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.settlement') }}</h4>
              <div class="summary-grid">
                <article><strong>{{ t('catalog.rental.settlement.rentalCharges') }}</strong><span>{{ agreement()!.settlement!.rentalCharges | number:'1.2-2' }}</span></article>
                <article><strong>{{ t('catalog.rental.settlement.lateFee') }}</strong><span>{{ agreement()!.settlement!.lateFeeAmount | number:'1.2-2' }}</span></article>
                <article><strong>{{ t('catalog.rental.settlement.damageCharge') }}</strong><span>{{ agreement()!.settlement!.damageChargeAmount | number:'1.2-2' }}</span></article>
                <article><strong>{{ t('catalog.rental.settlement.depositApplied') }}</strong><span>{{ agreement()!.settlement!.depositApplied | number:'1.2-2' }}</span></article>
                <article><strong>{{ t('catalog.rental.settlement.balanceDue') }}</strong><span>{{ agreement()!.settlement!.balanceDue | number:'1.2-2' }}</span></article>
              </div>
            </section>
          }

          @if (canHandover()) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.handover') }}</h4>
              <div class="form-grid">
                <label class="full"><span>{{ t('catalog.rental.lifecycle.checklist') }}</span><textarea pTextarea rows="3" [(ngModel)]="handoverForm.checklistJson"></textarea></label>
                <label class="full"><span>{{ t('catalog.rental.lifecycle.photos') }}</span><textarea pTextarea rows="2" [(ngModel)]="handoverForm.photoEvidenceJson"></textarea></label>
                <label><span>{{ t('catalog.rental.lifecycle.signature') }}</span><input pInputText [(ngModel)]="handoverForm.signatureReference" /></label>
                <label class="full">
                  <button pButton type="button" [label]="t('catalog.rental.action.handover')" [disabled]="acting()" (click)="handover()"></button>
                </label>
              </div>
            </section>
          }

          @if (canReturn()) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.return') }}</h4>
              <div class="form-grid">
                <label class="full"><span>{{ t('catalog.rental.lifecycle.checklist') }}</span><textarea pTextarea rows="3" [(ngModel)]="returnForm.checklistJson"></textarea></label>
                <label class="full"><span>{{ t('catalog.rental.lifecycle.photos') }}</span><textarea pTextarea rows="2" [(ngModel)]="returnForm.photoEvidenceJson"></textarea></label>
                <label><span>{{ t('catalog.rental.lifecycle.signature') }}</span><input pInputText [(ngModel)]="returnForm.signatureReference" /></label>
                <label><span>{{ t('catalog.rental.lifecycle.actualReturn') }}</span><input pInputText type="datetime-local" [(ngModel)]="returnForm.actualReturnLocal" /></label>
                <label class="full">
                  <button pButton type="button" [label]="t('catalog.rental.action.return')" [disabled]="acting()" (click)="returnItem()"></button>
                </label>
              </div>
            </section>
          }

          @if (canAssessDamage()) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.damage') }}</h4>
              <div class="form-grid">
                <label><span>{{ t('catalog.rental.lifecycle.damageAmount') }}</span><p-inputNumber [(ngModel)]="damageForm.damageAmount" [min]="0" /></label>
                <label><span>{{ t('catalog.rental.lifecycle.approvalThreshold') }}</span><p-inputNumber [(ngModel)]="damageForm.approvalThresholdAmount" [min]="0" /></label>
                <label class="full"><span>{{ t('catalog.rental.lifecycle.damageDescription') }}</span><textarea pTextarea rows="2" [(ngModel)]="damageForm.description"></textarea></label>
                <label class="full"><span>{{ t('catalog.rental.lifecycle.photos') }}</span><textarea pTextarea rows="2" [(ngModel)]="damageForm.photoEvidenceJson"></textarea></label>
                <label class="full">
                  <button pButton type="button" [label]="t('catalog.rental.action.assessDamage')" [disabled]="acting()" (click)="assessDamage()"></button>
                </label>
              </div>
            </section>
          }

          @if (canSettle()) {
            <section class="lifecycle-panel">
              <h4>{{ t('catalog.rental.lifecycle.settlement') }}</h4>
              <div class="form-grid">
                <label><span>{{ t('catalog.rental.settlement.rentalCharges') }}</span><p-inputNumber [(ngModel)]="settleForm.rentalCharges" [min]="0" /></label>
                <label><span>{{ t('catalog.rental.settlement.lateFee') }}</span><p-inputNumber [(ngModel)]="settleForm.lateFeeAmount" [min]="0" /></label>
                <label><span>{{ t('catalog.rental.settlement.damageCharge') }}</span><p-inputNumber [(ngModel)]="settleForm.damageChargeAmount" [min]="0" /></label>
                <label><span>{{ t('catalog.rental.settlement.depositApplied') }}</span><p-inputNumber [(ngModel)]="settleForm.depositApplied" [min]="0" /></label>
                <article class="full preview-balance">
                  <strong>{{ t('catalog.rental.settlement.balanceDue') }}</strong>
                  <span>{{ settlementPreview() | number:'1.2-2' }}</span>
                </article>
                <label class="full">
                  <button pButton type="button" [label]="t('catalog.rental.action.settle')" [disabled]="acting()" (click)="settle()"></button>
                </label>
              </div>
            </section>
          }

          @if (error()) {
            <p class="error">{{ error() }}</p>
          }
        }
      }
    </ain-page>
  `,
  styles: [`
    .header-card, .summary-grid, .form-grid { display: grid; gap: 12px; margin-bottom: 16px; }
    .header-card { grid-template-columns: 1fr auto; align-items: start; padding: 16px; border: 1px solid #e5e7eb; border-radius: 12px; }
    .actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
    .summary-grid { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
    .summary-grid article { display: grid; gap: 4px; padding: 12px; background: #f8fafc; border-radius: 8px; }
    .form-grid { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); }
    .form-grid label { display: grid; gap: 6px; }
    .form-grid label.full { grid-column: 1 / -1; }
    .lifecycle-panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 14px; margin-bottom: 12px; }
    .lifecycle-panel h4 { margin: 0 0 10px; font-size: 15px; }
    .preview-balance { display: flex; justify-content: space-between; padding: 10px 12px; background: #eff6ff; border-radius: 8px; }
    .error { color: #b91c1c; font-size: 13px; }
  `]
})
export class RentalAgreementDetailComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly acting = signal(false);
  readonly agreement = signal<RentalAgreement | null>(null);
  readonly units = signal<RentalAssetUnit[]>([]);
  readonly error = signal('');

  readonly isCreateMode = computed(() => this.route.snapshot.paramMap.get('id') === 'new');
  readonly unitOptions = computed(() => this.units().map(u => ({ label: u.unitCode, value: u.id })));

  form = {
    agreementNumber: '',
    customerReference: '',
    rentalAssetUnitId: '',
    depositAmount: 500,
    rentalRateAmount: 100
  };

  handoverForm = {
    checklistJson: '{"condition":"good","fuelLevel":"full","accessories":"complete"}',
    photoEvidenceJson: '[]',
    signatureReference: ''
  };

  returnForm = {
    checklistJson: '{"condition":"returned","notes":""}',
    photoEvidenceJson: '[]',
    signatureReference: '',
    actualReturnLocal: ''
  };

  damageForm = {
    damageAmount: 0,
    description: '',
    photoEvidenceJson: '[]',
    approvalThresholdAmount: 500
  };

  settleForm = {
    rentalCharges: 0,
    lateFeeAmount: 0,
    damageChargeAmount: 0,
    depositApplied: 0
  };

  ngOnInit(): void {
    this.service.listRentalUnits().subscribe({ next: units => {
      this.units.set(units);
      if (units[0]) this.form.rentalAssetUnitId = units[0].id;
    }});

    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }

    this.reloadAgreement();
  }

  reloadAgreement(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) { this.loading.set(false); return; }

    this.loading.set(true);
    this.service.getRentalAgreement(id).subscribe({
      next: agreement => {
        this.agreement.set(agreement);
        this.prefillSettleForm(agreement);
        this.loading.set(false);
      },
      error: () => { this.error.set(this.t('catalog.rental.notFound')); this.loading.set(false); }
    });
  }

  prefillSettleForm(agreement: RentalAgreement): void {
    const days = Math.max(1, Math.ceil(
      (new Date(agreement.endUtc).getTime() - new Date(agreement.startUtc).getTime()) / (1000 * 60 * 60 * 24)
    ));
    this.settleForm.rentalCharges = agreement.rentalRateAmount * days;
    this.settleForm.damageChargeAmount = agreement.damageAssessment?.damageAmount ?? 0;
    this.settleForm.depositApplied = agreement.depositAmount;
    if (!this.returnForm.actualReturnLocal) {
      this.returnForm.actualReturnLocal = this.toLocalInputValue(agreement.endUtc);
    }
  }

  settlementPreview(): number {
    const total = this.settleForm.rentalCharges + this.settleForm.lateFeeAmount + this.settleForm.damageChargeAmount;
    return Math.max(0, total - this.settleForm.depositApplied);
  }

  canHandover(): boolean {
    const a = this.agreement();
    return a?.status === 'Active' && !a.handover;
  }

  canReturn(): boolean {
    const a = this.agreement();
    return a?.status === 'HandedOver' && !a.returnChecklist;
  }

  canAssessDamage(): boolean {
    const a = this.agreement();
    return (a?.status === 'Returned' || a?.status === 'HandedOver') && !a?.damageAssessment && !a?.settlement;
  }

  canApproveDamage(): boolean {
    return this.agreement()?.damageAssessment?.status === 'PendingApproval';
  }

  canSettle(): boolean {
    const a = this.agreement();
    return a?.status === 'Returned' && !a.settlement;
  }

  save(): void {
    const start = new Date(); start.setDate(start.getDate() + 1);
    const end = new Date(start); end.setDate(end.getDate() + 7);
    this.saving.set(true);
    this.service.createRentalAgreement({
      rentalBookingId: null,
      rentalAssetUnitId: this.form.rentalAssetUnitId,
      agreementNumber: this.form.agreementNumber,
      customerReference: this.form.customerReference,
      startUtc: start.toISOString(),
      endUtc: end.toISOString(),
      depositAmount: this.form.depositAmount,
      rentalRateAmount: this.form.rentalRateAmount
    }).subscribe({
      next: created => { this.saving.set(false); void this.router.navigate(['/rental/agreements', created.id]); },
      error: () => { this.saving.set(false); this.error.set(this.t('catalog.rental.createFailed')); }
    });
  }

  handover(): void {
    const id = this.agreement()?.id;
    if (!id) return;
    this.acting.set(true);
    this.error.set('');
    this.service.handoverRentalAgreement(id, {
      checklistJson: this.handoverForm.checklistJson,
      photoEvidenceJson: this.handoverForm.photoEvidenceJson,
      signatureReference: this.handoverForm.signatureReference.trim() || 'handover-signature'
    }).subscribe({
      next: a => { this.agreement.set(a); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.rental.handoverFailed')); }
    });
  }

  returnItem(): void {
    const agreement = this.agreement();
    if (!agreement) return;
    const actualReturnUtc = this.returnForm.actualReturnLocal
      ? new Date(this.returnForm.actualReturnLocal).toISOString()
      : agreement.endUtc;
    this.acting.set(true);
    this.error.set('');
    this.service.returnRentalAgreement(agreement.id, {
      checklistJson: this.returnForm.checklistJson,
      photoEvidenceJson: this.returnForm.photoEvidenceJson,
      signatureReference: this.returnForm.signatureReference.trim() || 'return-signature',
      actualReturnUtc,
      lateFeeRuleId: null
    }).subscribe({
      next: a => { this.agreement.set(a); this.prefillSettleForm(a); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.rental.returnFailed')); }
    });
  }

  assessDamage(): void {
    const id = this.agreement()?.id;
    if (!id) return;
    this.acting.set(true);
    this.error.set('');
    this.service.assessRentalDamage(id, {
      damageAmount: this.damageForm.damageAmount,
      description: this.damageForm.description.trim(),
      photoEvidenceJson: this.damageForm.photoEvidenceJson,
      approvalThresholdAmount: this.damageForm.approvalThresholdAmount
    }).subscribe({
      next: () => { this.reloadAgreement(); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.rental.damageFailed')); }
    });
  }

  approveDamage(): void {
    const id = this.agreement()?.id;
    if (!id) return;
    this.acting.set(true);
    this.error.set('');
    this.service.approveRentalDamage(id).subscribe({
      next: () => { this.reloadAgreement(); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.rental.approveDamageFailed')); }
    });
  }

  settle(): void {
    const agreement = this.agreement();
    if (!agreement) return;
    this.acting.set(true);
    this.error.set('');
    this.service.settleRentalAgreement(agreement.id, {
      rentalCharges: this.settleForm.rentalCharges,
      lateFeeAmount: this.settleForm.lateFeeAmount,
      damageChargeAmount: this.settleForm.damageChargeAmount,
      depositApplied: this.settleForm.depositApplied
    }).subscribe({
      next: () => { this.reloadAgreement(); this.acting.set(false); },
      error: () => { this.acting.set(false); this.error.set(this.t('catalog.rental.settleFailed')); }
    });
  }

  toLocalInputValue(iso: string): string {
    const date = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  t(key: string): string { return this.localization.translate(key); }
}
